import type { SessionData } from '../types';
import { estimateTotalDuration } from '../utils/estimateDuration';

interface ReadyPhaseProps {
  session: SessionData;
  onStart: () => void;
}

function formatEstimatedDuration(session: SessionData): string {
  const totalSeconds = estimateTotalDuration(session);
  const minutes = Math.ceil(totalSeconds / 60);
  return `~${minutes} min`;
}

export function ReadyPhase({ session, onStart }: ReadyPhaseProps) {
  return (
    <div className="flex flex-col items-center gap-6 p-6">
      <h1
        className="text-heading-xl text-gray-900 dark:text-textDark-primary"
        data-testid="workout-ready-title"
      >
        Ready to Train
      </h1>

      <div className="w-full max-w-lg space-y-3">
        <div className="flex justify-between text-body text-gray-600 dark:text-textDark-secondary">
          <span>
            {session.rounds} round{session.rounds > 1 ? 's' : ''}
          </span>
          <span>{formatEstimatedDuration(session)}</span>
        </div>

        <ul
          className="divide-y divide-gray-200 dark:divide-dark-border rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface"
          data-testid="workout-exercise-list"
        >
          {session.exercises.map((ex) => (
            <li key={ex.id} className="flex items-center justify-between px-4 py-3">
              <span className="text-body font-medium text-gray-900 dark:text-textDark-primary">
                {ex.exerciseName}
              </span>
              <span className="text-meta text-gray-500 dark:text-textDark-muted">
                {ex.exerciseMode === 'TimeBased'
                  ? `${ex.targetDurationSeconds}s`
                  : `${ex.targetReps} reps`}
                {ex.suggestedWeightKg != null && ` · ${ex.suggestedWeightKg}kg`}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <button
        onClick={onStart}
        className="mt-4 w-full max-w-lg rounded-md bg-brand-600 px-6 py-4 text-lg font-semibold text-white transition-colors hover:bg-brand-700 active:bg-brand-800"
        data-testid="workout-start-button"
      >
        Start Session
      </button>

      <p className="mt-2 text-center text-xs text-gray-400 dark:text-textDark-muted max-w-lg leading-relaxed">
        These are suggested exercises, weights, and reps. Adjust anything to suit how you feel today. Stop any exercise if you experience pain or discomfort.
      </p>
    </div>
  );
}
